<template>
    <client-only>
        <portal to="limb" ref="portal">
            <div
                :class="classList"
                class="cases-panel elevate"
                :ref="`panel-${modalName}`"
            >
                <button
                    @click="close"
                    class="panel-close btn-bg elevate"
                    :title="`${$t('general.close')} (ESC)`"
                >
                    <span class="icon-ic-dismiss"/>
                </button>

                <transition v-if="!noTitle" :name="`fade-slide-${translate}`" mode="out-in">
                    <h2 :class="`panel-title ${titleClass || ''}`">
                        {{title}} <slot name="head" />
                    </h2>
                </transition>

                <div
                    class="drawer-content"
                    v-bsl="panelVisible"
                    :id="layoutId"
                    :ref="`content-${modalName}`"
                >
                    <slot v-if="panelVisible"/>
                </div>
            </div>
        </portal>
    </client-only>
</template>


<script>
// TODO: Ask before discard changes @close
import modalNavigation from '~/mixins/modalNavigation.js';
import { findScrollableParent } from '~/utilities/scroll.js';

export default {
    name: 'CasesPanel',

    mixins: [modalNavigation],

    props: ['modalName', 'classes', 'side', 'defTitle', 'noTitle', 'titleClass'],

    data () {
        return {
            modalType: 'drawer',
            emiters: [],
            emiter: 'cases-panel',
            title: this.defTitle,
            translate: 'left',
            changed: false,
            panelVisible: false,

            touchStartY: 0,
            touchEndY: 0,
            touchDeltaY: 0,
            touchStartX: 0,
            touchEndX: 0,
            touchDeltaX: 0,
            touchMoved: false,
            scrollableTarget: null,

            isSwiping: false
        }
    },

    computed: {
        open () {
            return this.$modalityStore.drawer == this.modalName;
        },

        classList () {
            let classList = [];
            if (this.classes) classList = [...this.classes];

            classList.push(this.position);

            if (this.open) classList.push('active');
            return classList;
        },

        position () {
            return this.side || 'left';
        },

        layoutId () {
            return `${this.modalName}-panel-layout`;
        }
    },

    watch: {
        '$modalityStore.drawer': {
            immediate: true,

            handler (val, oldVal) {
                if (!val && oldVal == this.modalName) {
                    this.afterClose();
                    this.$emit("hide");
                } else if (val == this.modalName) {
                    this.$emit("show");
                }
            }
        },

        open: {
            immediate: true,

            handler (val, oldVal) {
                if (val) {
                    this.panelVisible = true;
                    // Block body scroll and recrop content
                    if (process.client) {
                        setTimeout(() => {
                            this.$cropContent();
                        }, 100);
                    }
                }
                if (!val && oldVal && process.client) {
                    // Unblock Body scroll after panel is closed
                    setTimeout(() => {
                        if (!this.open) this.panelVisible = false;
                    }, 350)
                }
            }
        },

        panelVisible(val) {
            if (val) this.addSwipeListener();
            else this.removeSwipeListener();
        }
    },

    mounted () {
        this.$root.$on('change-panel-title', this.changeTitle);
    },

    methods: {
        close () {
            this.$modalityStore.closeModal({
                type: this.modalType,
                name: this.modalName
            });
            this.afterClose();
        },

        afterClose () {
            this.emiters.forEach(el => el.classList.remove('active'))
            this.emiters = [];
            this.$emit('close')
        },

        changeTitle (data) {
            if (data.name !== this.modalName) return true;
            this.title = data.title;
        },


        addSwipeListener () {
            this.$nextTick(() => {
                // Now get the actual panel and content inside the portal target
                this.panelEl = this.$refs[`panel-${this.modalName}`];
                this.contentEl = this.$refs[`content-${this.modalName}`];

                if (!this.panelEl || !this.contentEl) return;

                this.panelEl.addEventListener('touchstart', this.onTouchStart, { passive: true });
                this.panelEl.addEventListener('touchmove', this.onTouchMove, { passive: true });
                this.panelEl.addEventListener('touchend', this.onTouchEnd, { passive: true });
            });
        },

        removeSwipeListener () {
            if (!this.panelEl) return;

            this.panelEl.removeEventListener('touchstart', this.onTouchStart);
            this.panelEl.removeEventListener('touchmove', this.onTouchMove);
            this.panelEl.removeEventListener('touchend', this.onTouchEnd);

            this.panelEl = null;
            this.contentEl = null;
        },

        onTouchStart(e) {
            if (e.touches.length !== 1) return;

            this.isSwiping = true;

            this.touchStartY = e.touches[0].clientY;
            this.touchStartX = e.touches[0].clientX;
            this.touchMoved = false;
            this.touchDeltaY = 0;
            this.touchDeltaX = 0;

            const container = this.panelEl;
            const touchedElement = e.target;
            this.scrollableTarget = findScrollableParent(touchedElement, container);
        },

        onTouchMove (e) {
            if (!this.isSwiping) return;

            const touch = e.touches[0];
            this.touchDeltaY = touch.clientY - this.touchStartY;
            this.touchDeltaX = touch.clientX - this.touchStartX;

            const scrollable = this.scrollableTarget;
            const atTop = !scrollable || scrollable.scrollTop === 0;
            const isMobile = window.innerWidth < 576;

            if (isMobile) {
                // Only move panel if swiping down and content is at top
                if (this.touchDeltaY > 0 && atTop) {
                    this.touchMoved = true;
                    this.panelEl.classList.add('dragging');
                    this.panelEl.style.setProperty('transform', `translateY(${this.touchDeltaY}px)`, 'important');
                }
            } else {
                // Side panels for desktop

                this.touchMoved = true;
                this.panelEl.classList.add('dragging');

                
                const translateX = this.position === 'left' ? 
                                    Math.min(this.touchDeltaX, 0) : 
                                    Math.max(this.touchDeltaX, 0);

                this.panelEl.style.setProperty('transform', `translateX(${translateX}px)`, 'important');
            }
        },

        onTouchEnd () {
            if (!this.isSwiping) return;
            this.isSwiping = false;

            const isMobile = window.innerWidth < 576;
            const closeThreshold = 200;

            let shouldClose = false;

            if (isMobile) {
                const scrollable = this.scrollableTarget;
                const atTop = !scrollable || scrollable.scrollTop === 0;

                shouldClose = this.touchDeltaY > closeThreshold && atTop;
            } else if (this.position === 'left') {
                shouldClose = this.touchDeltaX < -closeThreshold;
            } else if (this.position === 'right') {
                shouldClose = this.touchDeltaX > closeThreshold;
            }

            // Reset transition style for snap back or closing
            this.panelEl.classList.remove('dragging');
            
            if (shouldClose) this.close();

            this.panelEl.style.transform = '';
            this.touchDeltaY = 0;
            this.touchDeltaX = 0;
            this.scrollableTarget = null;
        },
    },

    beforeDestroy () {
        this.$root.$off('change-panel-title', this.changeTitle);
        if (process.client) {
            this.removeSwipeListener();
        }
    }
}
</script>


<style>
.fade-slide-bottom-enter-active,
.fade-slide-top-enter-active,
.fade-slide-left-enter-active,
.fade-slide-right-enter-active {
  transition: transform .2s cubic-bezier(1.0, 0.7, 0.8, 1.0),
              opacity .2s cubic-bezier(1.0, 0.7, 0.8, 1.0);
}
.fade-slide-bottom-leave-active,
.fade-slide-top-leave-active,
.fade-slide-left-leave-active,
.fade-slide-right-leave-active {
  transition: transform .2s cubic-bezier(0.1, 0.8, 0.5, 1.0),
              opacity .2s cubic-bezier(0.1, 0.8, 0.5, 1.0);
}

.fade-slide-bottom-enter-to,
.fade-slide-bottom-leave,
.fade-slide-top-enter-to,
.fade-slide-top-leave,
.fade-slide-right-enter-to,
.fade-slide-right-leave,
.fade-slide-left-enter-to,
.fade-slide-left-leave {
    transform: translate(0, 0);
    opacity: 1;
}

.fade-slide-left-enter,
.fade-slide-left-leave-to {
    transform: translate(5px, 0);
    opacity: 0;
}

.fade-slide-right-enter,
.fade-slide-right-leave-to {
    transform: translate(-5px, 0);
    opacity: 0;
}

.fade-slide-top-enter,
.fade-slide-top-leave-to {
    transform: translate(0, 5px);
    opacity: 0;
}

.fade-slide-bottom-enter,
.fade-slide-bottom-leave-to {
    transform: translate(0, -5px);
    opacity: 0;
}

.fade-slide-left-leave-to {
    transform: translate(-5px, 0);
    opacity: 0;
}

.fade-slide-right-leave-to {
    transform: translate(5px, 0);
    opacity: 0;
}

.fade-slide-top-leave-to {
    transform: translate(0, -5px);
    opacity: 0;
}

.fade-slide-bottom-leave-to {
    transform: translate(0, -5px);
    opacity: 0;
}

</style>
